import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Sparkles } from "lucide-react";

/**
 * SiteNoticePopup
 * Small modern popup shown once per visitor (localStorage-gated).
 * Closable via the X button or Escape key.
 */
const STORAGE_KEY = "liftile_notice_v2_seen";
const NOTICE_TEXT =
  "LIFTILE Website is continuously being upgraded. Upcoming Version 3.0 will include AI Assistant, real project & machine images, drone videos, enhanced animations, improved UI/UX, and more technical information.";

export default function SiteNoticePopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        // Small delay so it doesn't fight the initial loading screen
        const t = setTimeout(() => setOpen(true), 1600);
        return () => clearTimeout(t);
      }
    } catch (e) {
      /* localStorage unavailable */
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => {
    setOpen(false);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      /* ignore */
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="notice-popup"
          data-testid="site-notice-popup"
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 30, scale: 0.96 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="fixed z-[1000] bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm"
        >
          <div className="relative overflow-hidden rounded-xl border border-liftile-orange/40 bg-liftile-matte-black shadow-[0_25px_70px_-15px_rgba(0,0,0,0.7)]">
            <div
              aria-hidden
              className="absolute inset-0 pointer-events-none opacity-[0.15]"
              style={{
                background:
                  "radial-gradient(circle at 15% 0%, rgba(255,107,26,0.35), transparent 55%)",
              }}
            />
            <div className="relative p-5 sm:p-6">
              <button
                type="button"
                onClick={close}
                aria-label="Close notice"
                data-testid="site-notice-close-btn"
                className="absolute top-3 right-3 grid place-items-center w-7 h-7 rounded-full text-liftile-cream/70 hover:text-white hover:bg-white/10 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>

              <div className="flex items-center gap-2 mb-2">
                <span className="grid place-items-center w-7 h-7 rounded-full bg-liftile-orange/15 border border-liftile-orange/40">
                  <Sparkles className="w-3.5 h-3.5 text-liftile-orange" />
                </span>
                <span className="font-heading uppercase tracking-[0.14em] text-[11px] text-liftile-orange font-semibold">
                  Website Notice
                </span>
              </div>

              <p className="font-body text-[13px] sm:text-sm text-liftile-cream/85 leading-relaxed pr-4">
                <span className="mr-1" aria-hidden>🚧</span>
                {NOTICE_TEXT}
              </p>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={close}
                  data-testid="site-notice-got-it-btn"
                  className="inline-flex items-center gap-2 bg-liftile-orange text-white font-heading text-[11px] uppercase tracking-wider font-semibold px-4 py-2 rounded-md hover:bg-liftile-orange-dark transition-colors"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
